import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { BookOpen, Compass, ClipboardList, Home, Menu, NotebookPen, Rocket, UserRound, X } from 'lucide-react';
import { useFounder } from './FounderContext';

export const founderNav = [
  { to: '/app/dashboard', label: 'Home', icon: Home },
  { to: '/app/roadmap', label: 'Roadmap', icon: ClipboardList },
  { to: '/app/journal', label: 'Build Journal', icon: NotebookPen },
  { to: '/app/learn', label: 'Learn', icon: BookOpen },
  { to: '/app/opportunities', label: 'Opportunities', icon: Compass },
  { to: '/app/profile', label: 'My Project', icon: UserRound },
];

export function FounderLogo({ onClick }) {
  const go = useNavigate();
  return <button type="button" onClick={onClick || (() => go('/'))} className="inline-flex items-center gap-2 text-left" aria-label="GROWTH home">
    <span className="w-10 h-10 rounded-xl growth-gradient flex items-center justify-center"><Rocket className="w-5 h-5 text-ink-900" /></span>
    <span className="font-heading text-xl font-extrabold tracking-tight">GROWTH<span className="block text-[10px] text-growth tracking-widest leading-3">FOUNDER PLATFORM</span></span>
  </button>;
}

function NavItem({ item, onClick, mobile = false }) {
  return <NavLink to={item.to} onClick={onClick} className={({ isActive }) => mobile
    ? `flex flex-col items-center gap-1 px-1 py-3 text-[10px] font-semibold ${isActive ? 'text-growth' : 'text-muted-foreground'}`
    : `flex items-center gap-2 rounded-xl px-3 py-2.5 font-semibold text-sm ${isActive ? 'bg-secondary text-growth' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'}`}>
    <item.icon className="h-4 w-4" aria-hidden="true" /> {item.label}
  </NavLink>;
}

export function FounderLayout({ children }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const { storageWarning } = useFounder();
  useEffect(() => {
    if (!open) return undefined;
    const closeOnEsc = e => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', closeOnEsc);
    return () => window.removeEventListener('keydown', closeOnEsc);
  }, [open]);
  return <div className="min-h-screen bg-background grid-bg">
    <a href="#founder-main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-ink-800 focus:p-3 focus:rounded-lg">Skip to main content</a>
    <header className="sticky top-0 z-40 glass border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 min-h-16 flex items-center justify-between gap-3">
        <FounderLogo onClick={() => nav('/app/dashboard')} />
        <nav className="hidden lg:flex gap-1" aria-label="Main navigation">{founderNav.map((item) => <NavItem item={item} key={item.to} />)}</nav>
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="founder-mobile-menu" aria-label="Toggle navigation" className="lg:hidden p-2 rounded-lg border border-border">{open ? <X /> : <Menu />}</button>
      </div>
      {open && <nav id="founder-mobile-menu" className="lg:hidden px-4 pb-4 grid grid-cols-2 gap-2" aria-label="Expanded navigation">{founderNav.map((item) => <NavItem item={item} key={item.to} onClick={() => setOpen(false)} />)}</nav>}
    </header>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4"><p className="text-xs rounded-xl border border-amber-400/20 bg-amber-400/5 text-amber-200/90 p-3">Local concept prototype: records remain in this browser. No account verification, independent evidence review, live partners or funding is available. Do not enter confidential business secrets.</p>{storageWarning && <p role="alert" className="mt-3 text-sm rounded-xl border border-red-400/40 bg-red-950/30 text-red-200 p-3">{storageWarning}</p>}</div>
    <main id="founder-main" tabIndex="-1" className="max-w-7xl mx-auto px-4 sm:px-6 py-8 pb-28 lg:pb-12">{children}</main>
    <footer className="border-t border-border py-6 px-4 pb-24 lg:pb-6 text-center text-xs text-muted-foreground">GROWTH Founder Platform · Local prototype · Founders retain ownership of their ideas · No funding or selection guarantee</footer>
    <nav className="fixed bottom-0 left-0 right-0 z-40 lg:hidden glass border-t border-border grid grid-cols-5" aria-label="Mobile navigation">{founderNav.slice(0,5).map((item) => <NavItem item={item} key={item.to} mobile />)}</nav>
  </div>;
}
